import {
  ComponentPublicInstance,
  computed,
  reactive,
  ref,
  toRefs,
  unref,
  UnwrapRef,
} from 'vue';
import type { AxiosPromise, AxiosError, AxiosResponse } from '@libs/http';
import { useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/vue-query';
import { MaybeRef, useInfiniteScroll } from '@vueuse/core';

export type UseInfiniteQueryConfig2<T> = UseInfiniteQueryOptions<
  AxiosPromise<T>,
  AxiosError,
  AxiosResponse<T>,
  any[]
> & {
  key: any[];
  immediate?: MaybeRef<boolean> | ((...arg: any[]) => boolean);
  onReject?: (...arg: any[]) => void;
  onSettled?: (...arg: any[]) => void;
  onSuccess?: (...arg: any[]) => void;
};

type BaseModel<T> = {
  pageNumber: number;
  pageSize: number;
  result: T[];
};

export function useInfiniteVueQuery<
  QueryType extends { items: UnwrapRef<ItemType[]> | null },
  ItemType
>(
  fetcher: (args: Record<string, any>) => AxiosPromise<QueryType>,
  {
    key = [],

    immediate = true,

    onReject = () => {},
    onSuccess = () => {},
    onSettled = () => {},
    select = undefined,
  }: UseInfiniteQueryConfig2<QueryType>
) {
  const BASE_MODEL: BaseModel<ItemType> = {
    pageNumber: 1,
    pageSize: 10,
    result: [],
  };

  const config = reactive({ ...BASE_MODEL });
  const loadMoreRef = ref<HTMLElement | ComponentPublicInstance>();

  const reset = () => {
    Object.assign(config, { ...BASE_MODEL });
  };

  const shouldFetch = computed(() =>
    typeof immediate === 'function' ? immediate() : unref(immediate)
  );

  const {
    isError,
    isSuccess,
    isLoading,
    isRefetching,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    fetchStatus,
    refetch,
    hasNextPage,
    status,
    ...query
  } = useInfiniteQuery(
    key,
    ({ pageParam = config.pageNumber }) =>
      fetcher({
        pageSize: config.pageSize,
        pageNumber: pageParam,
      }),
    {
      cacheTime: 0,
      staleTime: 0,
      onSuccess(result) {
        config.result = result.pages.reduce<UnwrapRef<ItemType[]>>(
          (previousValue, currentValue) => {
            if (currentValue.data.items) {
              previousValue.push(...currentValue.data.items);
            }

            return previousValue;
          },
          []
        );

        onSuccess(result);
      },
      onError: onReject,
      enabled: shouldFetch,
      keepPreviousData: true,
      getNextPageParam: (_lastPage, allPages) => {
        const lastPage = _lastPage as unknown as AxiosResponse<QueryType, any>;

        return lastPage.data.items &&
          lastPage.data.items.length >= config.pageSize
          ? allPages.length + 1
          : undefined;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 1,
      retryDelay: 1000,
      select,
    }
  );

  const container = computed<HTMLElement>(() => {
    if (loadMoreRef.value && '$el' in loadMoreRef.value) {
      return loadMoreRef.value.$el;
    }

    return loadMoreRef.value;
  });

  useInfiniteScroll(
    container,
    () => {
      if (container.value) {
        const { clientHeight } = container.value;

        if (
          clientHeight > 0 &&
          !isFetchingNextPage.value &&
          hasNextPage?.value
        ) {
          onSettled();

          fetchNextPage();
        }
      }
    },
    { distance: 10, behavior: 'smooth' }
  );

  return {
    execute: refetch,
    fetchNextPage,
    fetchPreviousPage,
    reset,

    isIdle: computed(() => fetchStatus.value === 'idle'),
    isPending: isLoading,
    isRevalidating: isRefetching,
    isError: isError,
    isSuccess: isSuccess,
    isRefetching: isRefetching,
    isFetchingNextPage,
    status,

    ...toRefs(config),
    hasNextPage,
    loadMoreRef,
    ...query,
  };
}
